import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Link, useHistory } from "react-router-dom";
import fetch from "../shared/Fetch";
import { ReactComponent as CheckedBoxIcon } from "../svgs/CheckboxChecked.svg";
import { ReactComponent as UncheckedBoxIcon } from "../svgs/CheckboxUnchecked.svg";
import { Button } from "./Atoms";

const FilteringBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5rem;
  gap: 3rem;
  width: max-content;
`;

const SectionContainer = styled.div`
  display: grid;
  grid-template-rows: max-content max-content;
  gap: 1.5rem;
`;

const SectionTitle = styled.span`
  font-size: 1.2rem;
`;

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  grid-auto-columns: max-content;
  gap: 0.5rem;
`;

const FilterPill = styled(Link)`
  cursor: pointer;
  display: grid;
  grid-template-columns: max-content 1fr;
  align-items: flex-start;
  justify-content: flex-start;
  transition: none;
  text-wrap: balanced;
  max-width: 12rem;
  font-weight: normal;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  font-size: inherit;
  color: inherit;
  text-decoration: none;
  &:focus {
    outline: none;
  }
  &:hover {
    color: var(--filter-button-hover-text-color);
    svg:first-child {
      fill: ${props =>
        props.selected
          ? "transparent"
          : "var(--filter-button-hovered-background)"};
    }
  }
  & svg:first-child {
    margin-right: 0.5rem;
    display: grid;
  }
`;

const FilterButton = styled(Button)`
  position: sticky;
  top: 0;
`;

const AreaGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const IndentedButtonsContainer = styled(ButtonsContainer)`
  padding-left: 1.5rem;
  margin-top: 0.25rem;
  font-size: 0.85rem;
`;

const TinyButton = styled(Link)`
  color: var(--button-primary-color);
  background: var(--button-background-color);
  border: 1px solid var(--button-primary-color);
  &:hover {
    color: var(--button-hover-color);
    border-color: var(--button-hover-color);
    background: var(--button-hover-background-color);
  }
  &:active {
    color: white;
    border-color: var(--button-hover-color);
    background-color: var(--button-hover-color);
  }
  &:focus {
    outline-color: var(--button-hover-color);
  }
  padding: 0.2rem 0.5rem;
  border-radius: 0.2rem;
  display: inline-block;
  margin-right: 0.5rem;
  margin-top: 0.5rem;
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: none;
`;

const FilteringBar = ({
  selectedGroups,
  selectedDepartments,
  selectedSeniorities,
  selectedServiceAreas,
  closeFilter,
  applyFilter,
  onNavigate
}) => {
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [seniorities, setSeniorities] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [pendingAreaClick, setPendingAreaClick] = useState(null);

  const history = useHistory();

  useEffect(() => {
    setPendingAreaClick(null);
  }, [selectedServiceAreas]);

  let useGroups = false;
  if (selectedGroups) {
    useGroups = true;
  }

  const areaGroupsMap = useMemo(() => {
    const map = {};
    serviceAreas.forEach(area => {
      const areaGroups = groups.filter(g => g.serviceAreaId === area.id);
      map[area.id] = { groups: areaGroups, ids: areaGroups.map(g => g.id) };
    });
    return map;
  }, [groups, serviceAreas]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchGroups = async () => {
    try {
      fetch(`/groups`).then(res => {
        const sortedGroups = res.sort((a, b) =>
          a.name.toLowerCase() < b.name.toLowerCase()
            ? -1
            : a.name.toLowerCase() > b.name.toLowerCase()
            ? 1
            : 0
        );
        setGroups(sortedGroups);
      });
    } catch (err) {
      console.log("Request resulted in an error", err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const showAll =
        (params.get("allLocations") || "").toLowerCase() === "true";
      const url = showAll ? `/departments` : `/departments?activeOnly=true`;
      fetch(url).then(res => {
        setDepartments(res);
      });
    } catch (err) {
      console.log("Request resulted in an error", err);
    }
  };

  const fetchSeniorities = async () => {
    try {
      fetch(`/seniorities`).then(res => {
        setSeniorities(res);
      });
    } catch (err) {
      console.log("Request resulted in an error", err);
    }
  };

  const fetchServiceAreas = async () => {
    try {
      fetch(`/serviceareas`).then(res => {
        setServiceAreas(res);
      });
    } catch (err) {
      console.log("Request resulted in an error", err);
    }
  };

  const fetchData = () => {
    fetchGroups();
    fetchDepartments();
    fetchSeniorities();
    fetchServiceAreas();
  };

  const getUpdatedDepartmentPath = id => {
    let pathname = window.location.pathname;
    let params = pathname.split("/");
    let departments = params[1];

    let departmentsArray = departments.split(",");
    if (departmentsArray.find(d => d === id)) {
      const filteredDepartments = departmentsArray.filter(d => d !== id);
      if (filteredDepartments.length > 0) {
        departmentsArray = filteredDepartments;
      }
    } else {
      const storedDepartments = [...departmentsArray];
      storedDepartments.push(id);
      departmentsArray = storedDepartments;
    }
    params[1] = departmentsArray.join(",");
    return params.join("/") + window.location.search;
  };

  const getAllDepartmentsPath = () => {
    let pathname = window.location.pathname;
    let params = pathname.split("/");

    params[1] = departments.map(d => d.id).join(","); // Reset departments to "all"
    return params.join("/") + window.location.search;
  };

  const getAllButOneDepartmentPath = () => {
    let pathname = window.location.pathname;
    let params = pathname.split("/");

    params[1] = departments.length > 0 ? departments[0].id : params[1];

    return params.join("/") + window.location.search;
  };

  const getUpdatedGroupPath = id => {
    let pathname = window.location.pathname;
    let params = pathname.split("/");
    let groups = params[2];

    let groupsArray = groups.split(",");
    if (groupsArray.find(g => g === id)) {
      const filteredGroups = groupsArray.filter(g => g !== id);
      if (filteredGroups.length === 0) {
        filteredGroups.push("all");
      }
      groupsArray = filteredGroups;
    } else {
      const storedGroups = [...groupsArray];
      storedGroups.push(id);
      groupsArray = storedGroups.filter(g => g !== "all");
    }

    params[2] = groupsArray.length > 0 ? groupsArray.join(",") : groups;

    return params.join("/") + window.location.search;
  };

  const getUpdatedSeniorityPath = id => {
    let pathname = window.location.pathname;
    let params = pathname.split("/");
    let seniorities = params[3];

    let senioritiesArray = seniorities.split(",");
    if (senioritiesArray.find(s => s === id)) {
      const filteredSeniorities = senioritiesArray.filter(s => s !== id);
      if (filteredSeniorities.length === 0) {
        filteredSeniorities.push("all");
      }
      senioritiesArray = filteredSeniorities;
    } else {
      const storedSeniorities = [...senioritiesArray];
      storedSeniorities.push(id);
      senioritiesArray = storedSeniorities.filter(s => s !== "all");
    }

    params[3] = senioritiesArray.length > 0 ? senioritiesArray.join(",") : "";

    return params.join("/") + window.location.search;
  };

  const getAllSenioritiesPath = () => {
    let pathname = window.location.pathname;
    let params = pathname.split("/");

    params[3] = seniorities.map(s => s.order.toString()).join(","); // Reset seniorities to "all"
    return params.join("/") + window.location.search;
  };

  const getEmptySenioritiesPath = () => {
    let pathname = window.location.pathname;
    let params = pathname.split("/");
    params[3] = "all"; // Reset seniorities to "all"
    return params.join("/") + window.location.search;
  };

  const getUpdatedServiceAreaWithGroupsPath = (areaId, areaGroupIds) => {
    let pathname = window.location.pathname;
    let params = pathname.split("/");

    // Toggle service area
    let areasArray = params[4].split(",");
    const wasSelected = !!areasArray.find(a => a === areaId);
    if (wasSelected) {
      const filtered = areasArray.filter(a => a !== areaId);
      areasArray = filtered.length === 0 ? ["all"] : filtered;
    } else {
      areasArray = areasArray.filter(a => a !== "all");
      areasArray.push(areaId);
    }
    params[4] = areasArray.join(",");

    // Sync groups in this area
    if (areaGroupIds.length > 0) {
      let groupsArray =
        params[2] === "all"
          ? []
          : params[2].split(",").filter(g => g !== "all");
      if (wasSelected) {
        groupsArray = groupsArray.filter(g => !areaGroupIds.includes(g));
      } else {
        areaGroupIds.forEach(gid => {
          if (!groupsArray.includes(gid)) groupsArray.push(gid);
        });
      }
      params[2] = groupsArray.length === 0 ? "all" : groupsArray.join(",");
    }

    return params.join("/") + window.location.search;
  };

  const getSearchParams = () => new URLSearchParams(window.location.search);
  const isQueryFlagActive = key => {
    const params = getSearchParams();
    const v = (params.get(key) || "").toLowerCase();
    return v === "true";
  };
  const getUpdatedQueryFlagPath = key => {
    const params = new URLSearchParams(window.location.search);
    const current = (params.get(key) || "").toLowerCase();
    if (current === "true") {
      params.set(key, "false");
    } else {
      params.set(key, "true");
    }
    const qs = params.toString();
    return window.location.pathname + (qs ? "?" + qs : "");
  };

  function applyFilterAndClose() {
    applyFilter();
    if (closeFilter) closeFilter();
  }

  return (
    <FilteringBarContainer>
      {applyFilter && (
        <FilterButton onClick={applyFilterAndClose} type="button">
          Oppdater og lukk filter
        </FilterButton>
      )}
      <SectionContainer>
        <SectionTitle>Filtrer etter tjenesteområde</SectionTitle>
        <ButtonsContainer>
          {serviceAreas.map((area, i) => {
            const { groups: areaGroups, ids: areaGroupIds } = useGroups
              ? areaGroupsMap[area.id] || { groups: [], ids: [] }
              : { groups: [], ids: [] };
            const urlSelected =
              selectedServiceAreas &&
              !!selectedServiceAreas.find(a => a === area.id);
            const areaSelected =
              pendingAreaClick === area.id ? !urlSelected : urlSelected;
            return (
              <AreaGroup key={area.id + "" + i}>
                <FilterPill
                  as="button"
                  type="button"
                  selected={areaSelected}
                  onClick={() => {
                    setPendingAreaClick(area.id);
                    if (onNavigate) onNavigate();
                    const newPath = getUpdatedServiceAreaWithGroupsPath(
                      area.id,
                      areaGroupIds
                    );
                    setTimeout(() => history.push(newPath), 0);
                  }}
                >
                  {areaSelected ? <CheckedBoxIcon /> : <UncheckedBoxIcon />}
                  {area.name}
                </FilterPill>
                {areaGroups.length > 0 && (
                  <IndentedButtonsContainer>
                    {areaGroups.map((group, j) => {
                      const groupSelected =
                        selectedGroups &&
                        !!selectedGroups.find(g => g === group.id);
                      return (
                        <FilterPill
                          key={group.id + "" + j}
                          to={getUpdatedGroupPath(group.id)}
                          selected={groupSelected}
                        >
                          {groupSelected ? (
                            <CheckedBoxIcon />
                          ) : (
                            <UncheckedBoxIcon />
                          )}
                          {group.name}
                        </FilterPill>
                      );
                    })}
                  </IndentedButtonsContainer>
                )}
              </AreaGroup>
            );
          })}
          {useGroups && groups.filter(g => !g.serviceAreaId).length > 0 && (
            <AreaGroup>
              <IndentedButtonsContainer>
                {groups
                  .filter(g => !g.serviceAreaId)
                  .map((group, j) => {
                    const groupSelected =
                      selectedGroups &&
                      !!selectedGroups.find(g => g === group.id);
                    return (
                      <FilterPill
                        key={group.id + "" + j}
                        to={getUpdatedGroupPath(group.id)}
                        selected={groupSelected}
                      >
                        {groupSelected ? (
                          <CheckedBoxIcon />
                        ) : (
                          <UncheckedBoxIcon />
                        )}
                        {group.name}
                      </FilterPill>
                    );
                  })}
              </IndentedButtonsContainer>
            </AreaGroup>
          )}
        </ButtonsContainer>
      </SectionContainer>
      <SectionContainer>
        <SectionTitle>Filtrer etter stillingsnivå</SectionTitle>
        <TinyButton
          to={
            selectedSeniorities.length === seniorities.length
              ? getEmptySenioritiesPath()
              : getAllSenioritiesPath()
          }
          style={{ textDecoration: "none" }}
        >
          {selectedSeniorities.length === seniorities.length
            ? "Fjern alle"
            : "Markér alle"}
        </TinyButton>
        <ButtonsContainer>
          {seniorities.map((seniority, i) => (
            <FilterPill
              key={seniority.id + "" + i}
              to={getUpdatedSeniorityPath(seniority.order.toString())}
              selected={
                selectedSeniorities &&
                !!selectedSeniorities.find(
                  s => s.toString() === seniority.order.toString()
                )
              }
            >
              {selectedSeniorities &&
              !!selectedSeniorities.find(
                s => s.toString() === seniority.order.toString()
              ) ? (
                <CheckedBoxIcon />
              ) : (
                <UncheckedBoxIcon />
              )}

              {seniority.name}
            </FilterPill>
          ))}
        </ButtonsContainer>
      </SectionContainer>
      <SectionContainer>
        <SectionTitle>Filtrer etter lokasjon</SectionTitle>
        <TinyButton
          to={
            selectedDepartments.length === departments.length
              ? getAllButOneDepartmentPath()
              : getAllDepartmentsPath()
          }
          style={{ textDecoration: "none" }}
        >
          {selectedDepartments.length === departments.length
            ? "Fjern alle"
            : "Markér alle"}
        </TinyButton>
        <ButtonsContainer>
          {departments.map((department, i) => (
            <FilterPill
              key={department.id + "" + i}
              to={getUpdatedDepartmentPath(department.id)}
              selected={
                selectedDepartments &&
                !!selectedDepartments.find(d => d === department.id)
              }
            >
              {selectedDepartments &&
              !!selectedDepartments.find(d => d === department.id) ? (
                <CheckedBoxIcon />
              ) : (
                <UncheckedBoxIcon />
              )}
              {department.name}
            </FilterPill>
          ))}
        </ButtonsContainer>
      </SectionContainer>
      {(() => {
        const parts = (window.location.pathname || "").split("/");
        const inner = parts && parts.length > 5 ? parts[5] : "";
        const show =
          inner === "consultants" ||
          inner === "reconciliation" ||
          inner === "reconreport";
        return show ? (
          <SectionContainer>
            <SectionTitle>Andre filtre</SectionTitle>
            <ButtonsContainer>
              <FilterPill
                to={getUpdatedQueryFlagPath("vacation")}
                selected={isQueryFlagActive("vacation")}
              >
                {isQueryFlagActive("vacation") ? (
                  <CheckedBoxIcon />
                ) : (
                  <UncheckedBoxIcon />
                )}
                På ferie i perioden
              </FilterPill>
              <FilterPill
                to={getUpdatedQueryFlagPath("overbooked")}
                selected={isQueryFlagActive("overbooked")}
              >
                {isQueryFlagActive("overbooked") ? (
                  <CheckedBoxIcon />
                ) : (
                  <UncheckedBoxIcon />
                )}
                Overbooket i perioden
              </FilterPill>
              <FilterPill
                to={getUpdatedQueryFlagPath("onleave")}
                selected={isQueryFlagActive("onleave")}
              >
                {isQueryFlagActive("onleave") ? (
                  <CheckedBoxIcon />
                ) : (
                  <UncheckedBoxIcon />
                )}
                I permisjon i perioden
              </FilterPill>
              <FilterPill
                to={getUpdatedQueryFlagPath("hideFullAbsence")}
                selected={isQueryFlagActive("hideFullAbsence")}
              >
                {isQueryFlagActive("hideFullAbsence") ? (
                  <CheckedBoxIcon />
                ) : (
                  <UncheckedBoxIcon />
                )}
                Skjul 100% fravær i perioden
              </FilterPill>
            </ButtonsContainer>
          </SectionContainer>
        ) : null;
      })()}
    </FilteringBarContainer>
  );
};

export default FilteringBar;
